// =============================================================================
// Painel White Label — Gestão de Instâncias de Provider
// Layout padronizado (ListPageLayout) com KPIs clicáveis, filtros rápidos na
// barra, filtros avançados no Sheet "Mais" e PaginationFooter (50→500).
//
// @version 2.2.0
// RELEASE: 2026-05-30 v2.2.0 - Filtro rapido de Tenant movido para a barra
//                              principal usando WlTenantFilter (popover +
//                              busca server-side, padrao da auditoria WL).
//                              Removido do Sheet "Mais" e do carregamento
//                              local de tenants.
// RELEASE: 2026-05-29 v2.1.0 - Removida edicao manual (botao lapis + Dialog).
//                              Instancias sao gerenciadas apenas por contratacoes.
// RELEASE: 2026-05-28 v2.0.0 - Migrado para o shell ListPageLayout/KpiCard.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Server,
  Search,
  ListChecks,
  CalendarPlus,
  CheckCircle2,
  Clock,
  RotateCcw,
  XCircle,
  RefreshCw,
} from "lucide-react";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from "@/components/layout/list-page";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import { useWlInstanceKpis } from "@/hooks/useWlListKpis";
import { WlTenantFilter } from "@/components/wl/filters/WlTenantFilter";
import { cn } from "@/lib/utils";

interface WlInstanceRow {
  id: string;
  tenant_id: string;
  tenant_name: string | null;
  tenant_slug: string | null;
  provider_id: string;
  provider_name: string | null;
  // provider_slug removido — sigiloso do root admin.

  instance_id: string | null;
  instance_name: string | null;
  status: string;
  source_type: string;
  payment_status: string | null;
  inbox_id: string | null;
  notes: string | null;
  requested_at: string | null;
  activated_at: string | null;
  released_at: string | null;
  created_at: string;
  total_count: number;
}

interface TenantOption { id: string; name: string }
interface ProviderOption { id: string; display_name: string | null; name: string }

const STATUS_OPTIONS = ["pending", "in_use", "released", "cancelled"] as const;
const SOURCE_OPTIONS = ["manual", "plan", "addon"] as const;
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

// Mapas de cor (bolinha + texto preto)
const STATUS_DOT: Record<string, string> = {
  in_use: "bg-green-500",
  pending: "bg-yellow-500",
  released: "bg-blue-500",
  cancelled: "bg-zinc-400",
};
const STATUS_LABEL: Record<string, string> = {
  in_use: "Em uso",
  pending: "Pendente",
  released: "Liberada",
  cancelled: "Cancelada",
};
const SOURCE_DOT: Record<string, string> = {
  manual: "bg-zinc-400",
  plan: "bg-violet-500",
  addon: "bg-amber-500",
};

/** Bolinha colorida + texto preto (padrão visual). */
function DotLabel({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
      <span className={cn("h-2 w-2 rounded-full shrink-0", color)} />
      {label}
    </span>
  );
}


export default function WlInstances() {
  const { toast } = useToast();

  const [rows, setRows] = useState<WlInstanceRow[]>([]);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // tenantFilter: null = Todos (padrao WlTenantFilter)
  const [tenantFilter, setTenantFilter] = useState<string | null>(null);
  const [providerFilter, setProviderFilter] = useState("__all");
  const [statusFilter, setStatusFilter] = useState("__all");
  const [sourceFilter, setSourceFilter] = useState("__all");
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);


  // Carrega lista de providers para o filtro avancado
  // (tenants sao carregados internamente pelo WlTenantFilter via wl_list_tenants)
  useEffect(() => {
    (async () => {
      const p = await supabase.rpc("wl_list_api_providers");
      setProviders((p.data || []) as ProviderOption[]);
    })();
  }, []);

  /** Status efetivo aplicado à RPC: KPI tem prioridade sobre o Select. */
  const effectiveStatus = useMemo(() => {
    if (kpiFilter && kpiFilter !== "created_today") return kpiFilter;
    return statusFilter === "__all" ? null : statusFilter;
  }, [statusFilter, kpiFilter]);

  /** Filtros para KPI (exclui status quando há card ativo, mantendo totais globais). */
  const kpiFilters = useMemo(() => {
    const base = {
      tenant_id: tenantFilter,
      provider_id: providerFilter === "__all" ? null : providerFilter,
      status: statusFilter === "__all" ? null : statusFilter,
      source_type: sourceFilter === "__all" ? null : sourceFilter,
      search: search || null,
    } as any;
    if (kpiFilter && kpiFilter !== "created_today") base.status = null;
    return base;
  }, [tenantFilter, providerFilter, statusFilter, sourceFilter, search, kpiFilter]);

  const { data: kpis, isLoading: kpisLoading } = useWlInstanceKpis(kpiFilters);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("wl_list_provider_instances", {
        p_tenant_id: tenantFilter,
        p_provider_id: providerFilter === "__all" ? null : providerFilter,
        p_status: effectiveStatus,
        p_source_type: sourceFilter === "__all" ? null : sourceFilter,
        p_search: search || null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      const list = (data || []) as WlInstanceRow[];
      setRows(list);
      setTotal(list.length ? Number(list[0].total_count) : 0);
    } catch (e: any) {
      toast({ title: "Erro ao carregar instâncias", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, tenantFilter, providerFilter, statusFilter, sourceFilter, kpiFilter, search]);

  const toggleKpi = (key: string | null) => {
    setKpiFilter((prev) => (prev === key ? null : key));
    setPage(1);
  };


  /** Conta filtros avancados ativos (provider + origem). Tenant agora vive na barra principal. */
  const moreCount = useMemo(() => {
    let n = 0;
    if (providerFilter !== "__all") n++;
    if (sourceFilter !== "__all") n++;
    return n;
  }, [providerFilter, sourceFilter]);

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Instâncias"
        description="Gestão de instâncias de provider"
        actions={
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        }
      />

      <ListPageKpis columns={6}>
        <KpiCard color="purple" icon={ListChecks} label="Total" value={kpisLoading ? "…" : kpis?.total ?? 0}
          onClick={() => toggleKpi(null)} active={kpiFilter === null} />
        <KpiCard color="cyan" icon={CalendarPlus} label="Criadas Hoje" value={kpisLoading ? "…" : kpis?.created_today ?? 0}
          onClick={() => toggleKpi("created_today")} active={kpiFilter === "created_today"} />
        <KpiCard color="green" icon={CheckCircle2} label="Em Uso" value={kpisLoading ? "…" : kpis?.in_use ?? 0}
          onClick={() => toggleKpi("in_use")} active={kpiFilter === "in_use"} />
        <KpiCard color="yellow" icon={Clock} label="Pendentes" value={kpisLoading ? "…" : kpis?.pending ?? 0}
          onClick={() => toggleKpi("pending")} active={kpiFilter === "pending"} />
        <KpiCard color="blue" icon={RotateCcw} label="Liberadas" value={kpisLoading ? "…" : kpis?.released ?? 0}
          onClick={() => toggleKpi("released")} active={kpiFilter === "released"} />
        <KpiCard color="red" icon={XCircle} label="Canceladas" value={kpisLoading ? "…" : kpis?.cancelled ?? 0}
          onClick={() => toggleKpi("cancelled")} active={kpiFilter === "cancelled"} />
      </ListPageKpis>

      <ListPageFiltersBar
        isFetching={loading}
        moreCount={moreCount}
        onMoreClick={() => setMoreOpen(true)}
      >
        <div className="relative flex-1 min-w-[200px] sm:flex-none sm:w-[260px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Tenant, instance_id ou nome…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { setSearch(searchInput); setPage(1); }
            }}
            onBlur={() => { if (searchInput !== search) { setSearch(searchInput); setPage(1); } }}
          />
        </div>
        <WlTenantFilter
          value={tenantFilter}
          onChange={(id) => { setTenantFilter(id); setPage(1); }}
        />
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setKpiFilter(null); setPage(1); }}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[140px]")}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos status</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {loading ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Server className="h-10 w-10 opacity-40" />
            Nenhuma instância encontrada
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium">Tenant</TableHead>
                <TableHead className="text-xs font-medium">Provider</TableHead>
                <TableHead className="text-xs font-medium">Instance ID</TableHead>
                <TableHead className="text-xs font-medium">Nome</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="text-xs font-medium">Origem</TableHead>
                <TableHead className="text-xs font-medium">Pagamento</TableHead>
                <TableHead className="text-xs font-medium">Criada</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="py-2 text-xs">
                    <div className="font-medium text-foreground">{r.tenant_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{r.tenant_slug}</div>
                  </TableCell>
                  <TableCell className="py-2 text-xs">{r.provider_name || "—"}</TableCell>
                  <TableCell className="py-2 font-mono text-[11px]">{r.instance_id || "—"}</TableCell>
                  <TableCell className="py-2 text-xs">{r.instance_name || "—"}</TableCell>
                  <TableCell className="py-2">
                    <DotLabel
                      color={STATUS_DOT[r.status] || "bg-zinc-400"}
                      label={STATUS_LABEL[r.status] || r.status}
                    />
                  </TableCell>
                  <TableCell className="py-2">
                    <DotLabel
                      color={SOURCE_DOT[r.source_type] || "bg-zinc-400"}
                      label={r.source_type}
                    />
                  </TableCell>
                  <TableCell className="py-2 text-xs">{r.payment_status || "—"}</TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(r.created_at).toLocaleDateString("pt-BR")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

        )}
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListPageFooter>

      {/* Sheet de filtros avançados */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Filtros avançados</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Provider</Label>
              <Select value={providerFilter} onValueChange={(v) => { setProviderFilter(v); setPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Todos</SelectItem>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.display_name || p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Origem</Label>
              <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Todas</SelectItem>
                  {SOURCE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" size="sm" onClick={() => {
                setProviderFilter("__all"); setSourceFilter("__all"); setPage(1);
              }}>Limpar</Button>
              <Button size="sm" onClick={() => setMoreOpen(false)}>Aplicar</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

    </ListPageLayout>
  );
}
